import { useEffect, useState } from 'react';
import { CheckCircleIcon, WarningIcon, XIcon } from '@phosphor-icons/react';
import { FileUpload01 } from '@admin-panel/components';
import { Button, Drawer, Input } from '@admin-panel/ui';
import type { OccasionFormValues, UploadAssetFile } from '../../types';

type OccasionFormDrawerProps = {
  open: boolean;
  mode: 'create' | 'edit';
  parentOptions: Array<{ value: number; label: string }>;
  initialValues: OccasionFormValues;
  imageFileList: Array<UploadAssetFile>;
  iconLightFileList: Array<UploadAssetFile>;
  iconDarkFileList: Array<UploadAssetFile>;
  isCreating: boolean;
  isUpdating: boolean;
  isUploadingImage: boolean;
  onClose: () => void;
  onSubmit: (values: OccasionFormValues) => Promise<void>;
  onImageFileListChange: (fileList: Array<UploadAssetFile>) => void;
  onIconLightFileListChange: (fileList: Array<UploadAssetFile>) => void;
  onIconDarkFileListChange: (fileList: Array<UploadAssetFile>) => void;
  onUploadStart: () => void;
  onUploadEnd: () => void;
};

const OccasionFormDrawer = ({
  open,
  mode,
  parentOptions,
  initialValues,
  imageFileList,
  iconLightFileList,
  iconDarkFileList,
  isCreating,
  isUpdating,
  isUploadingImage,
  onClose,
  onSubmit,
  onImageFileListChange,
  onIconLightFileListChange,
  onIconDarkFileListChange,
  onUploadStart,
  onUploadEnd,
}: OccasionFormDrawerProps) => {
  const [values, setValues] = useState<OccasionFormValues>({
    name: '',
    parent_id: undefined,
    is_active: true,
  });
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    setValues({
      name: initialValues.name ?? '',
      parent_id: initialValues.parent_id ?? undefined,
      is_active: initialValues.is_active ?? true,
    });
    setNameError(null);
  }, [initialValues, open]);

  const isSaving = isCreating || isUpdating || isUploadingImage;

  return (
    <Drawer.Root
      open={open}
      onOpenChange={nextOpen => (!nextOpen ? onClose() : undefined)}
      direction="right"
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-1000 bg-black/35 backdrop-blur-sm data-[state=closed]:opacity-0 data-[state=open]:opacity-100" />
        <Drawer.Content className="fixed inset-y-0 right-0 z-1000 flex h-full w-[min(96vw,460px)] flex-col border-l border-border/70 bg-background outline-none data-[state=closed]:translate-x-full data-[state=open]:translate-x-0">
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <div>
              <h2 className="m-0 text-base font-semibold text-foreground">
                {mode === 'edit' ? 'Edit Occasion' : 'Create Occasion'}
              </h2>
              <p className="m-0 text-xs text-muted-foreground">
                Manage taxonomy node details and media.
              </p>
            </div>
            <Drawer.Close asChild>
              <Button variant="ghost" size="small" icon={<XIcon size={16} />} aria-label="Close">
                Close
              </Button>
            </Drawer.Close>
          </header>

          <form
            className="min-h-0 flex-1 overflow-y-auto p-4"
            onSubmit={async event => {
              event.preventDefault();
              const normalizedName = values.name.trim();
              if (!normalizedName) {
                setNameError('Occasion name is required');
                return;
              }
              setNameError(null);
              await onSubmit({
                name: normalizedName,
                parent_id: values.parent_id ?? undefined,
                is_active: values.is_active ?? true,
              });
            }}
          >
            <div className="space-y-5">
              <section className="rounded-xl border border-border bg-muted/20 p-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="m-0 text-sm font-semibold text-foreground">Status</p>
                    <p className="m-0 text-xs text-muted-foreground">
                      Inactive nodes are editable and reorderable but not active in workflows.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setValues(prev => ({ ...prev, is_active: !prev.is_active }))}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      values.is_active === false
                        ? 'border-amber-300 bg-amber-50 text-amber-700'
                        : 'border-emerald-300 bg-emerald-50 text-emerald-700'
                    }`}
                  >
                    {values.is_active === false ? (
                      <WarningIcon size={14} />
                    ) : (
                      <CheckCircleIcon size={14} />
                    )}
                    {values.is_active === false ? 'Inactive' : 'Active'}
                  </button>
                </div>
              </section>

              {values.is_active === false ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  Inactive occasion: this node remains visible for curation.
                </div>
              ) : null}

              <section className="space-y-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="occasion-name">
                  Name
                </label>
                <Input
                  id="occasion-name"
                  placeholder="Occasion name"
                  value={values.name}
                  aria-invalid={nameError ? 'true' : undefined}
                  onChange={event => setValues(prev => ({ ...prev, name: event.target.value }))}
                />
                {nameError ? <p className="m-0 text-xs text-destructive">{nameError}</p> : null}
              </section>

              <section className="space-y-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="occasion-parent">
                  Parent Occasion
                </label>
                <select
                  id="occasion-parent"
                  className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                  value={values.parent_id ?? ''}
                  onChange={event =>
                    setValues(prev => ({
                      ...prev,
                      parent_id: event.target.value ? Number(event.target.value) : undefined,
                    }))
                  }
                >
                  <option value="">No parent = root occasion</option>
                  {parentOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </section>

              <section>
                <FileUpload01
                  usage="CATALOG"
                  title="Occasion image"
                  description="Upload the primary visual shown for this occasion."
                  fileList={imageFileList}
                  onChange={onImageFileListChange}
                  onUploadStart={onUploadStart}
                  onUploadEnd={onUploadEnd}
                />
              </section>

              <section>
                <FileUpload01
                  usage="ICON"
                  title="Icon light"
                  description="Upload the light-mode icon used in occasion surfaces."
                  fileList={iconLightFileList}
                  onChange={onIconLightFileListChange}
                  onUploadStart={onUploadStart}
                  onUploadEnd={onUploadEnd}
                />
              </section>

              <section>
                <FileUpload01
                  usage="ICON"
                  title="Icon dark"
                  description="Upload the dark-mode icon used in occasion surfaces."
                  fileList={iconDarkFileList}
                  onChange={onIconDarkFileListChange}
                  onUploadStart={onUploadStart}
                  onUploadEnd={onUploadEnd}
                />
              </section>
            </div>

            <footer className="sticky right-0 bottom-0 left-0 mt-6 flex items-center justify-end gap-2 border-t border-border bg-background/95 px-0 pt-4 backdrop-blur">
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" isLoading={isSaving} isDisabled={isUploadingImage}>
                {mode === 'edit' ? 'Update' : 'Create'}
              </Button>
            </footer>
          </form>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default OccasionFormDrawer;
